import { useState } from 'react';
import { User, Mail, Phone, RefreshCw, Save, MapPin, Settings, Bell, Sparkles, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';
import { userApi } from '@/services/api';
import { useToast } from '@/components/ui/toast';
import { SubscriptionStatusCard } from '@/components/subscription/SubscriptionStatusCard';
import { BaseAirportModal } from '@/components/dashboard/BaseAirportModal';
import { Link } from 'react-router-dom';

export function SettingsPage() {
  const { user, updateUser } = useAuthStore();
  const { addToast } = useToast();
  
  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [showBaseModal, setShowBaseModal] = useState(false);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const saveProfile = async () => {
    setIsSavingProfile(true);
    try {
      await userApi.updateProfile(profileData);
      updateUser(profileData);
      addToast({
        title: '保存成功',
        description: '个人信息已更新',
        variant: 'success',
      });
    } catch (error) {
      addToast({
        title: '保存失败',
        description: '请稍后重试',
        variant: 'error',
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">设置</h1>

      {/* Subscription */}
      <SubscriptionStatusCard />

      {user?.isStaff && (
        <Card>
          <CardContent className="py-4">
            <Link to="/admin/subscription">
              <Button variant="outline" className="w-full">
                <Settings className="w-4 h-4 mr-2" />
                管理订阅
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* AI & Push & Vault Settings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-4">
            <Link to="/settings/ai">
              <Button variant="outline" className="w-full">
                <Sparkles className="w-4 h-4 mr-2" />
                AI 设置
              </Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <Link to="/settings/push">
              <Button variant="outline" className="w-full">
                <Bell className="w-4 h-4 mr-2" />
                Bark 推送设置
              </Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <Link to="/settings/vault">
              <Button variant="outline" className="w-full">
                <BookOpen className="w-4 h-4 mr-2" />
                Vault 同步设置
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            个人信息
          </CardTitle>
          <CardDescription>管理您的基本资料</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">显示名称</Label>
              <Input
                id="displayName"
                name="displayName"
                value={profileData.displayName}
                onChange={handleProfileChange}
                placeholder="请输入显示名称"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">邮箱</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  placeholder="请输入邮箱"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">手机号</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="phone"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                  placeholder="请输入手机号"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="baseAirport">基地机场</Label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="baseAirport"
                    value={user?.baseAirport || ''}
                    placeholder="未设置"
                    readOnly
                    className="pl-10 bg-gray-50"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBaseModal(true)}
                >
                  {user?.baseAirport ? '修改' : '设置'}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveProfile} disabled={isSavingProfile}>
              {isSavingProfile ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              保存
            </Button>
          </div>
        </CardContent>
      </Card>

      <BaseAirportModal
        isOpen={showBaseModal}
        onClose={() => setShowBaseModal(false)}
      />
    </div>
  );
}
